import { withFormik } from 'formik';
import qs from 'qs';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import mapProps from 'recompose/mapProps';
import withState from 'recompose/withState';
import withHandlers from 'recompose/withHandlers';
import lifecycle from 'recompose/lifecycle';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import findKey from 'lodash/findKey';
import mapKeys from 'lodash/mapKeys';
import isEmpty from 'lodash/isEmpty';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import invert from 'lodash/invert';
import remove from 'lodash/remove';
import { fetchShippingStates, getCurrentRegionStates } from '../Shipping/duck';
import {
  getCountryCallingCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js';
import { submitUserDetails } from 'src/common/facades/eagle/checkout';
import { getCookies } from '../../../../js/facades/eagle/helpers/cookies';
import { localiseUrl } from 'src/common/utils/format-localised-url';
import {
  createCountryOptions,
  selectedCountry,
} from './helpers';
import getErrorMessages from 'src/common/utils/error-messages';
import { getCheckoutDetails } from '../../../store/thunks/checkoutDetails';
import { getUserDetails, checkoutDetails } from '../../../store/selectors/checkoutDetails';
import { trackClickField, trackCheckoutSubmitButton, trackErrors } from '../../../analytics/category/checkout';

const addressKeys = {
  firstname: 'firstName',
  lastname: 'lastName',
  address1: 'streetAddress',
  address2: 'streetAddress2',
  zipcode: 'postCode',
  phone_number: 'phone'
};

const withShippingDetails = compose(
  connect(
    state => ({
      existingUser: getUserDetails(state),
      checkoutDetails: checkoutDetails(state),
      isDataLoading: get(checkoutDetails(state), 'isLoading', true),
    }),
    dispatch => ({
      getCheckoutDetails: () => dispatch(getCheckoutDetails(getCookies())),
    })
  ),
  withRouter,
  withFormik({
    enableReinitialize: true,
    mapPropsToValues: props => {
      const formFields = get(props, 'form.body') || [];
      const countryIso = props.country.iso.toUpperCase();
      const currentCountryCode = getCountryCallingCode(countryIso);
      const marketingOptInField = formFields.find(field => field.primary.name === 'marketing_opt_in') || {};
      const existingUserEmail = get(props, 'existingUser.email', '');
      const existingUserAddress = get(props, 'existingUser.addresses[0]', {});

      const checkoutDetails =  get(props, 'checkoutDetails.checkout') || {};
      const shippingAddress = checkoutDetails.shippingAddress || {};
      const shippingEmail = checkoutDetails.email || '';

      let marketingOptIn = get(marketingOptInField, 'primary.isPreChecked') || false;

      if (existingUserEmail) {
        // Set the field value to null in case a user already marked to opt-in. This way the field will be
        // omitted later, and not displayed to the user.
        marketingOptIn = checkoutDetails.marketingOptIn ? null : false;
      }

      // use the values (from the user's input details), that Eagle sends back in order to pre-populate the form
      const mapKeysToValues = (values, email = '') => (omitBy({
        ...mapKeys(values, (value, key) => (invert(addressKeys)[key] || key)),
        base: '',
        marketing_opt_in: marketingOptIn,
        email,
      }, isNil));

      // check if user has already submitted shipping address details and return those
      if (!isEmpty(shippingAddress)) {
        return mapKeysToValues(shippingAddress, shippingEmail);
      }

      // check if user has existing shipping details and return those
      if (!isEmpty(existingUserAddress)) {
        return mapKeysToValues(existingUserAddress, existingUserEmail);
      }

      // Check for user input data first (if user has gone back to change their details - the details need to be prefilled)
      // If there is no user input data, get the existing user if they are logged in
      return mapKeysToValues({
        base: '',
        firstname: '',
        lastname: '',
        email: '',
        phone_number: `+${currentCountryCode}`,
        country: countryIso,
        state: '',
        address1: '',
        address2: '',
        city: '',
        zipcode: '',
        marketing_opt_in: marketingOptIn
      });
    },
    handleSubmit: (payload, actions) => {
      const props = actions.props;
      trackCheckoutSubmitButton('Shipping Details', event.type);
      // on submit, call the facade and on success -> redirect to /checkout/shipping-methods

      // map the payload keys, so that they match eagle, without manually updating UserForm
      const address = mapKeys(payload, (value, key) => (addressKeys[key] || key));

      const signup = {
        marketingOptIn: payload.marketing_opt_in
      };

      submitUserDetails({ address, signup }, getCookies())
        .then(() => {
          const returnUrl = qs.parse(props.history.location.search, { ignoreQueryPrefix: true }).return_to;
          if (returnUrl) {
            return window.location = returnUrl;
          }
          window.location = localiseUrl(props.country, '/checkout-v2');
        })
        .catch(error => {
          trackErrors(`Shipping Details form - ${get(error, 'details[0].message', error.description)}`);
          actions.setSubmitting(false);
          let errors = getErrorMessages('general', error.details, props.model.prismicGlobalContentModel.error);

          // map the error keys, so that they match eagle
          errors = mapKeys(errors, (value, key) => (invert(addressKeys)[key] || key));

          actions.setErrors(errors);
        });
    },
  }),
  connect(
    (state, props) => ({
      currentCountryStates: getCurrentRegionStates(state, props.values.country),
    }),
    dispatch => ({
      updateShippingStates: country => dispatch(fetchShippingStates(country)),
    })
  ),
  withProps(props => {
    const countries = createCountryOptions(props); // makes an array of label/value for the country select

    return { countries };
  }),
  lifecycle({
    componentDidMount() {
      const props = this.props;
      props.updateShippingStates(props.country.iso);
      props.getCheckoutDetails();
    },
    componentDidUpdate(prevProps) {
      const props = this.props;

      if (prevProps.values.country !== props.values.country) {
        props.updateShippingStates(props.values.country);
      }
    }
  }),
  withState('selectedCountry', 'setSelectedCountry', props => props.country.iso),
  withHandlers({
    handleClick: () => event => {
      if (event.target.id === 'marketing_opt_in') {
        trackClickField(event.target.name, event.target.checked);
      } else {
        trackClickField(event.target.name);
      }
    },
    onCountryChange: props => event => {
      props.setFieldValue('state', '');
      props.updateShippingStates(event.target.value);
      props.handleChange(event);
      trackClickField(event.target.name);
    },
    onFlagCountryChange: props => event => {
      props.setSelectedCountry(event.target.value);
      props.setFieldValue('phone_number', `+${getCountryCallingCode(event.target.value)}`);
    },
    onPhoneNumberChange: props => event => {
      let countryPrefix = '';
      const phoneNumber = parsePhoneNumberFromString(event.target.value);
      if (phoneNumber) {
        countryPrefix = phoneNumber.country;
        if (!countryPrefix) {
          let countryCallingCode = phoneNumber.countryCallingCode;
          const countries = phoneNumber.metadata.countries;
          countryPrefix = findKey(countries, function (arr) {
            return arr[0] === countryCallingCode;
          });
        }
      }
      if (event.target.value.length > 5) {
        props.setSelectedCountry(countryPrefix);
      }
      props.handleChange(event);
    }
  }),
  mapProps(props => {
    const formFields = (get(props, 'form.body') || []).map(field => {
      // Add state options to "state" formField
      if (get(field, 'primary.name') === 'state') {
        return {
          ...field,
          items: get(props, 'currentCountryStates', []).map(countryState => ({
            label: countryState.name,
            value: countryState.abbr
          })),
        };
      }

      return field;
    });

    const existingUserEmail = get(props, 'existingUser.email', '');
    const checkoutDetails =  get(props, 'checkoutDetails.checkout') || {};

    if (existingUserEmail && checkoutDetails.marketingOptIn) {
      remove(formFields, field => get(field, 'primary.name') === 'marketing_opt_in');
    }

    return {
      ...props,
      form: {
        ...props.form,
        body: formFields
      },
      flagCountry: selectedCountry(props, props.selectedCountry || props.country.iso) || {},
    };
  }),
);

export default withShippingDetails;



// WEBPACK FOOTER //
// ./src/client/js/view/components/UserForm/withShippingDetails.js